import { useTranslator } from '@Localization';
import { countries, TCountryCode } from 'countries-list';
import { ReactElement, ReactNode } from 'react';
import { IGColumnProps } from '../../../components/table/IGColumnProps';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { IAdminUserInfo } from '../../user/user.store';
import { ActionsContainer } from '../containers/ActionsContainer';

interface IProps {
  onResetClicked: (userId: string) => void;
}

export const useAdminUserListSchema = (props: IProps): IGColumnProps<IAdminUserInfo>[] => {
  const { t } = useTranslator();

  const formatDateTime = (value: string): string => {
    return toLocaleDateTimeString(value);
  };

  const formatTimeSpan = (timeSpan: string): string => {
    if (timeSpan.includes('.')) {
      return timeSpan.substring(0, timeSpan.lastIndexOf('.'));
    }
    return timeSpan;
  };

  const formatCountry = (model: IAdminUserInfo): ReactNode => {
    if (!model.country) {
      return <></>;
    }
    const country = countries[model.country as TCountryCode];
    return country ? country.name : '';
  };

  const formatBool = (value: boolean): ReactNode => t(value ? 'yes' : 'no');

  const formatFileSize = (value: number): ReactNode => {
    if (value) {
      return value > 1024 ? `${(value / 1024).toFixed(2)} GB` : `${value.toFixed(2)} MB`;
    }

    return '0 MB';
  };

  const renderActions = (adminUserInfo: IAdminUserInfo): ReactElement => {
    return (
      <ActionsContainer
        adminUserInfo={adminUserInfo}
        onResetClicked={props.onResetClicked}
      />
    );
  };

  return [
    {
      sortBy: 'email',
      headerName: t('email'),
      renderer: (x) => <span title={x.email}>{x.email}</span>,
      sticky: true,
    },
    {
      sortBy: 'name',
      headerName: t('name'),
      renderer: (x) => x.name,
    },
    {
      sortBy: 'organization',
      headerName: t('organization'),
      renderer: (x) => x.organization,
    },
    {
      sortBy: 'country',
      headerName: t('country'),
      renderer: (x): ReactNode => formatCountry(x),
    },
    {
      sortBy: 'registrationDate',
      headerName: t('registration_date'),
      renderer: (x) => formatDateTime(x.registrationDate),
    },
    {
      sortBy: 'lastLoginDate',
      headerName: t('last_login_date'),
      renderer: (x) => formatDateTime(x.lastLoginDate),
    },
    {
      sortBy: 'workTime',
      headerName: t('worktime'),
      renderer: (x) => formatTimeSpan(x.workTime),
    },
    {
      sortBy: 'role',
      headerName: t('role'),
      renderer: (x) => x.role,
    },
    {
      sortBy: 'plan',
      headerName: t('plan'),
      renderer: (x) => x.plan,
    },
    {
      sortBy: 'customLimits',
      headerName: t('custom_limits'),
      renderer: (x): ReactNode => formatBool(x.customLimits),
    },
    {
      sortBy: 'isActive',
      headerName: t('active'),
      renderer: (x): ReactNode => formatBool(x.isActive),
    },
    {
      sortBy: 'isEmailConfirmed',
      headerName: t('email_confirmed'),
      renderer: (x): ReactNode => formatBool(x.isEmailConfirmed),
    },
    {
      sortBy: 'dataSizeInMb',
      headerName: t('files_size'),
      renderer: (x): ReactNode => formatFileSize(x.dataSizeInMb),
    },
    {
      headerName: t('actions'),
      renderer: (x) => renderActions(x),
      width: 'min-content',
      sticky: true,
      actions: true,
    },
  ];
};
